import {
  Modal,
  Stack,
  Box,
  Typography,
  IconButton,
  TextField,
  Alert,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { desktopSx } from "src/utils/styles/modalStyles";
import { FormEvent, useCallback, useState } from "react";
import { VisitType } from "src/api/types";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { useGetProductsQuery } from "src/api/services/productsApi";
import { useCreateIntakeMutation } from "src/api/services/intakesApi";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import { useNavigate } from "react-router-dom";

const visitTypes: VisitType[] = [
  "Cancellation",
  "Check-in",
  "Initial",
  "Refill",
  "Upsell",
];

interface Props {
  open: boolean;
  onClose: () => void;
  productSlug: string;
}

const AddNewProductIntake = ({ onClose, open, productSlug }: Props) => {
  const [createIntake, { isLoading }] = useCreateIntakeMutation();
  const logError = useSentryLogger("error");
  const [error, setError] = useState("");
  const [visitType, setVisitType] = useState<VisitType>("Initial");
  const navigate = useNavigate();

  const { product } = useGetProductsQuery(
    { slug: productSlug },
    {
      selectFromResult: ({ data }) => {
        return {
          product: data?.data.find((p) => p.slug === productSlug) ?? null,
        };
      },
    },
  );

  const handleVisitType = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setVisitType(event.target.value as VisitType);
      setError("");
    },
    [],
  );

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (product == null) {
        setError("Product is required");
        return;
      }

      try {
        const intake = await createIntake({
          product_id: product.id,
          product_category_id: product.category.id,
          visit_type: visitType,
          active: false,
          product_slug: product.slug,
        }).unwrap();
        onClose();
        navigate(`${intake.id}/builder`);
      } catch (err) {
        logError("Error creating intake", err);
        setError("Failed to create intake. Please try again.");
      }
    },
    [createIntake, logError, navigate, onClose, product, visitType],
  );

  return (
    <Modal open={open}>
      <Stack
        sx={desktopSx}
        gap={2}
        component="form"
        maxWidth={400}
        onSubmit={handleSubmit}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="h5" fontWeight={700}>
            Add Intake
          </Typography>
          <IconButton onClick={onClose} sx={{ p: 0 }}>
            <CloseIcon fontSize="medium" />
          </IconButton>
        </Box>
        <Stack gap={2}>
          <TextField label="Product" value={product?.name ?? ""} disabled />
          <TextField
            select
            label="Visit Type"
            required
            value={visitType}
            onChange={handleVisitType}
          >
            {visitTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
          <LoadingButton type="submit" loading={isLoading}>
            Create intake
          </LoadingButton>
        </Stack>
        {error ? <Alert severity={"error"}>{error}</Alert> : null}
      </Stack>
    </Modal>
  );
};

export default AddNewProductIntake;
